/**
 * @file WmsSettingsPage.tsx
 * @description «Настройки склада» (phase13.0; разделы — phase19.23 этап A;
 * phase19.31 зона «Склад» — переведена целиком на Commora):
 *   - Правила: порядок списания партий, маркировка, отрицательные остатки,
 *     скан ячейки при сборке, пороги срока годности, зависшие черновики;
 *   - Устройства: принтеры, сканеры, весы, кассы склада;
 *   - Рабочее место: звук скана — настройка этого устройства;
 *   - Этикетки: принтер, листы самоклейки и калибровка (phase19.22).
 *
 * Страница отвечает за вкладки (`Tabs`, линия снизу) и выбор склада — он
 * первой строкой формы вкладки («Склад ▾»), а не отдельно справа. Заголовок
 * с «Сохранить» рисует вкладка (`WmsSettingsHeader`): только она знает, есть
 * ли что сохранять. Форма — колонкой до 720px.
 *
 * Без складов страница НЕ пустая: «Рабочее место» от склада не зависит, и
 * выключить звук скана нужно уметь до того, как заведён первый склад.
 */

import { Settings } from "lucide-react";
import { useEffect, useState } from "react";

import { useHasPermission } from "../../../auth/hooks";
import { EmptyState } from "../../../components/ui/EmptyState";
import { Label } from "../../../components/ui/label";
import { LIST_FRAME_CLASS, LIST_SURFACE_CLASS, ListError } from "../../../components/ui/ListStates";
import { SelectField } from "../../../components/ui/SelectField";
import { Skeleton } from "../../../components/ui/skeleton";
import { Tabs } from "../../../components/ui/Tabs";
import { WarehouseDevicesPanel } from "../../../components/wms/settings/WarehouseDevicesPanel";
import { WarehouseLabelsPanel } from "../../../components/wms/settings/WarehouseLabelsPanel";
import { WarehousePoliciesPanel } from "../../../components/wms/settings/WarehousePoliciesPanel";
import { WmsSettingsHeader } from "../../../components/wms/settings/WmsSettingsHeader";
import { WorkplacePanel } from "../../../components/wms/settings/WorkplacePanel";
import { warehousesApi, type WarehouseDto } from "../../../lib/api-wms";
import { wmsErrorText } from "../../../lib/wms-error-text";
import { cn } from "@/lib/utils";

type Tab = "policies" | "devices" | "workplace" | "labels";

/** Вкладки — данными; новая вкладка = запись. */
const TABS: { value: Tab; label: string }[] = [
  { value: "policies", label: "Правила" },
  { value: "devices", label: "Устройства" },
  { value: "workplace", label: "Рабочее место" },
  { value: "labels", label: "Этикетки" },
];

export function WmsSettingsPage() {
  const [warehouses, setWarehouses] = useState<WarehouseDto[]>([]);
  const [active, setActive] = useState<WarehouseDto | null>(null);
  const [tab, setTab] = useState<Tab>("policies");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  // Калибровку листов и принтер сохраняет только администратор склада.
  const canManage = useHasPermission("SHOP_WMS_MANAGE");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    warehousesApi
      .list()
      .then((data) => {
        if (cancelled) return;
        setWarehouses(data);
        setError(null);
        setActive((prev) => prev ?? data.find((w) => w.isDefault) ?? data[0] ?? null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(wmsErrorText(err, "Не удалось загрузить склады. Проверьте связь и попробуйте ещё раз."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  // «Склад ▾» — первая строка формы вкладки, которой нужен склад.
  const picker = (
    <div className="flex flex-col gap-1.5 pb-2">
      <Label htmlFor="wms-settings-warehouse">Склад</Label>
      <SelectField
        id="wms-settings-warehouse"
        value={active?.id ?? ""}
        onChange={(next) => setActive(warehouses.find((w) => w.id === next) ?? null)}
        options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
        placeholder="Выберите склад"
        ariaLabel="Склад"
        className="w-full sm:w-[320px]"
      />
    </div>
  );

  const body = (() => {
    if (tab === "workplace") return <WorkplacePanel />;
    if (loading) {
      return (
        <>
          <WmsSettingsHeader />
          <div className={cn("flex max-w-[45rem] flex-col gap-3 p-4", LIST_FRAME_CLASS)} aria-busy="true">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-12 rounded-lg" />
            ))}
          </div>
        </>
      );
    }
    if (error) {
      return (
        <>
          <WmsSettingsHeader />
          <ListError message={error} onRetry={() => setAttempt((n) => n + 1)} />
        </>
      );
    }
    if (!active) {
      return (
        <>
          <WmsSettingsHeader />
          <EmptyState
            className={LIST_SURFACE_CLASS}
            icon={Settings}
            title="Сначала создайте склад"
            description="Правила, устройства и этикетки задаются для конкретного склада — заведите его в разделе «Склады»."
          />
        </>
      );
    }
    // key по складу: сбрасывает форму при смене склада, иначе значения
    // предыдущего склада ушли бы в новый.
    if (tab === "policies") {
      return <WarehousePoliciesPanel key={active.id} warehouse={active} onUpdated={setActive} warehousePicker={picker} />;
    }
    if (tab === "devices") return <WarehouseDevicesPanel key={active.id} warehouseId={active.id} warehousePicker={picker} />;
    return (
      <WarehouseLabelsPanel
        key={active.id}
        warehouseId={active.id}
        canManage={canManage}
        onGoToDevices={() => setTab("devices")}
        warehousePicker={picker}
      />
    );
  })();

  return (
    <div className="flex flex-col gap-4">
      <Tabs aria-label="Разделы настроек склада" items={TABS} value={tab} onChange={setTab} />
      {body}
    </div>
  );
}
